import { useCallback, useMemo, useState } from 'react';
import type { NavigateFunction } from 'react-router';
import { AppRoutes } from '@/constants/appRoutes';
import type { MatterChatNavigationState } from './matterChatNavigationState';

interface UseMatterChatAddFilesParams {
  caseId: string | undefined;
  chatId: string | undefined;
  locationState: MatterChatNavigationState | undefined;
  navigate: NavigateFunction;
  handleUploadFilesClick: () => void;
}

export function useMatterChatAddFiles({
  caseId,
  chatId,
  locationState,
  navigate,
  handleUploadFilesClick,
}: UseMatterChatAddFilesParams) {
  const isMatterChat = Boolean(caseId && chatId);
  const [isAddMatterFilesModalVisible, setIsAddMatterFilesModalVisible] = useState(false);

  const matterDocumentIdsInChat = useMemo(
    () => locationState?.matterDocumentIdsInChat ?? [],
    [locationState?.matterDocumentIdsInChat],
  );

  const handleAddMatterFilesClick = useCallback(() => {
    setIsAddMatterFilesModalVisible(true);
  }, []);

  const handleHideAddMatterFilesModal = useCallback(() => {
    setIsAddMatterFilesModalVisible(false);
  }, []);

  const handleSelectMatterFilesFromCase = useCallback(() => {
    if (!caseId || !chatId) return;

    setIsAddMatterFilesModalVisible(false);
    navigate(`/${AppRoutes.Cases}/${caseId}`, {
      state: {
        addMatterFilesToChatId: chatId,
        matterDocumentIdsInChat,
        matterDocumentNamesById: locationState?.matterDocumentNamesById,
      } satisfies MatterChatNavigationState,
    });
  }, [caseId, chatId, locationState?.matterDocumentNamesById, matterDocumentIdsInChat, navigate]);

  const handleUploadMatterFilesFromComputer = useCallback(() => {
    setIsAddMatterFilesModalVisible(false);
    handleUploadFilesClick();
  }, [handleUploadFilesClick]);

  const matterChatInputProps = isMatterChat
    ? {
        onAddMatterFilesClick: handleAddMatterFilesClick,
        isAddMatterFilesModalVisible,
        onHideAddMatterFilesModal: handleHideAddMatterFilesModal,
        onSelectMatterFilesFromCase: handleSelectMatterFilesFromCase,
        onUploadMatterFilesFromComputer: handleUploadMatterFilesFromComputer,
      }
    : {};

  return { isMatterChat, matterChatInputProps };
}
